import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bot, Plus, Pause, Play, Skull, ChevronDown, ChevronUp, Beaker, Rocket } from "lucide-react";
import { ComposedChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { fmtUSD, gainClass } from "@/lib/format";

const STRATEGIES = [
  { id: "sma_cross", label: "SMA Cross", params: { fast: 10, slow: 30 } },
  { id: "rsi_mean_reversion", label: "RSI Mean Reversion", params: { window: 14, oversold: 30, overbought: 70 } },
  { id: "macd", label: "MACD", params: { fast: 12, slow: 26, signal: 9 } },
  { id: "bollinger", label: "Bollinger Bands", params: { window: 20, k: 2.0 } },
  { id: "vwap", label: "VWAP Trend", params: { window: 20, band_pct: 0.2 } },
  { id: "ai_council", label: "AI Council (5 agents)", params: {} },
];

const SYMBOLS = {
  crypto: ["BTC", "ETH", "SOL", "DOGE", "LINK"],
  stock: ["SPY", "AAPL", "NVDA", "TSLA", "MSFT", "AMD"],
  future: ["MES", "MNQ", "ES", "NQ"],
};

const dot = { running: "bg-emerald-500", paused: "bg-amber-500", killed: "bg-gray-400" };

export default function Bots() {
  const [bots, setBots] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showWizard, setShowWizard] = useState(false);

  const load = useCallback(() => {
    api.get("/bots").then(({ data }) => setBots(data)).catch(() => {});
    api.get("/bots/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-8" data-testid="bots-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <Bot size={24} /> AutoPilot
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Create a bot → backtest → paper deploy → monitor → stop anytime. AI proposes, the risk engine decides.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="rounded-full border-emerald-300 text-emerald-700">PAPER ONLY</Badge>
          <Button data-testid="create-bot-btn" onClick={() => setShowWizard((v) => !v)}
            className="rounded-full bg-brand text-white hover:bg-brand-deep">
            <Plus size={14} className="mr-1.5" /> Create Bot
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="autopilot-summary">
          <Tile label="Active bots" value={summary.active_bots} testId="summary-active" />
          <Tile label="Open positions" value={summary.open_positions} testId="summary-positions" />
          <Tile label="Today P&L" value={fmtUSD(summary.today_pnl)} cls={gainClass(summary.today_pnl)} testId="summary-today" />
          <Tile label="Entries 24h" value={summary.entries_24h} testId="summary-entries" />
          <Tile label="Exits 24h" value={summary.exits_24h} testId="summary-exits" />
          <Tile label="Council signals" value={summary.council_signals_24h} testId="summary-council" />
          <Tile label="Risk rejections" value={summary.risk_rejections_24h} testId="summary-rejections" />
        </div>
      )}

      {showWizard && <CreateBotWizard onDone={() => { setShowWizard(false); load(); }} />}

      <div className="grid md:grid-cols-2 gap-5" data-testid="bots-list">
        {bots === null ? (
          <p className="text-sm text-gray-400">Loading bots…</p>
        ) : bots.length ? (
          bots.map((b) => <BotCard key={b._id} bot={b} onChanged={load} />)
        ) : (
          <Card className="border border-dashed border-gray-300 rounded-2xl p-8 text-center md:col-span-2" data-testid="bots-empty">
            <Bot size={28} className="mx-auto text-gray-300" />
            <p className="text-sm text-gray-500 mt-2">No bots yet. Create your first AutoPilot bot — it trades paper money with your rules.</p>
          </Card>
        )}
      </div>
    </div>
  );
}

const Tile = ({ label, value, cls = "", testId }) => (
  <Card className="border border-gray-200 rounded-2xl p-3" data-testid={testId}>
    <p className="text-[11px] text-gray-500">{label}</p>
    <p className={`text-lg font-semibold ${cls}`}>{value}</p>
  </Card>
);

function BotCard({ bot, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const s = bot.stats || {};

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/bots/${bot._id}/${action}`);
      toast.success(action === "kill" ? "Bot killed — position liquidated" : `Bot ${action}d`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleDetail = async () => {
    if (!open) {
      const { data } = await api.get(`/bots/${bot._id}`).catch(() => ({ data: null }));
      setDetail(data);
    }
    setOpen((v) => !v);
  };

  return (
    <Card className="border border-gray-200 rounded-2xl p-5" data-testid={`bot-card-${bot._id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className={`w-2.5 h-2.5 rounded-full ${dot[bot.status] || "bg-gray-300"}`} data-testid={`bot-status-${bot._id}`} />
          <div>
            <p className="font-semibold text-sm">{bot.name}</p>
            <p className="text-[11px] text-gray-500">
              {bot.symbol} · {bot.asset_type} · {STRATEGIES.find((x) => x.id === bot.strategy)?.label || bot.strategy} · {bot.status}
              {bot.pause_reason === "daily_loss_limit" && <span className="text-amber-600 font-semibold"> (daily loss limit)</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {bot.status === "running" && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => act("pause")}
              data-testid={`bot-pause-${bot._id}`} className="h-8 w-8 p-0 rounded-full"><Pause size={13} /></Button>
          )}
          {bot.status === "paused" && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => act("resume")}
              data-testid={`bot-resume-${bot._id}`} className="h-8 w-8 p-0 rounded-full text-emerald-600"><Play size={13} /></Button>
          )}
          {bot.status !== "killed" && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => act("kill")}
              data-testid={`bot-kill-${bot._id}`} className="h-8 w-8 p-0 rounded-full text-rose-600"><Skull size={13} /></Button>
          )}
          <Button size="sm" variant="ghost" onClick={toggleDetail}
            data-testid={`bot-details-${bot._id}`} className="h-8 w-8 p-0 rounded-full">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-4 text-center">
        <Mini label="Today" value={fmtUSD(s.today_pnl || 0)} cls={gainClass(s.today_pnl || 0)} />
        <Mini label="Total" value={`${s.total_return_pct >= 0 ? "+" : ""}${s.total_return_pct ?? 0}%`} cls={gainClass(s.total_return_pct || 0)} />
        <Mini label="Win rate" value={`${s.win_rate ?? 0}%`} />
        <Mini label="Max DD" value={`-${s.max_drawdown_pct ?? 0}%`} />
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        {s.trades_count ?? 0} trades · PF {s.profit_factor ?? 0} · expectancy {fmtUSD(s.expectancy || 0)} · capital {fmtUSD(bot.capital)}
        {s.position && <span className="text-brand-deep font-medium"> · holding {s.position.qty} @ {fmtUSD(s.position.avg_price)}</span>}
        {bot.partial_take_profits?.length > 0 && (
          <span data-testid={`bot-scaleout-${bot._id}`}> · scale-out {bot.tp_steps_done || 0}/{bot.partial_take_profits.length}</span>
        )}
      </p>
      {bot.strategy === "ai_council" && bot.council_last && (
        <p className="text-[11px] text-gray-500 mt-1 bg-brand-tint/50 rounded-lg p-2" data-testid={`bot-council-${bot._id}`}>
          Council: <b>{String(bot.council_last.signal).toUpperCase()}</b> ({Math.round((bot.council_last.confidence || 0) * 100)}%) — {bot.council_last.explanation}
        </p>
      )}

      {open && detail && (
        <div className="mt-4 border-t border-gray-100 pt-3 space-y-3" data-testid={`bot-detail-${bot._id}`}>
          <BotEquityChart botId={bot._id} />
          {bot.strategy !== "ai_council" && (
            <OptimizerPanel bot={bot} onChanged={onChanged} />
          )}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Recent activity</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {(detail.events || []).slice(0, 12).map((e) => (
                <p key={e._id} className="text-[11px] text-gray-600">
                  <span className={`font-semibold ${e.kind === "risk_rejected" ? "text-rose-600" : e.kind === "entry" ? "text-emerald-600" : e.kind === "exit" ? "text-blue-600" : "text-gray-500"}`}>
                    {e.kind}
                  </span>{" "}
                  {e.message} <span className="text-gray-300">· {new Date(e.created_at).toLocaleString()}</span>
                </p>
              ))}
              {!detail.events?.length && <p className="text-[11px] text-gray-400">No activity yet.</p>}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Recent trades</p>
            {(detail.trades || []).slice(0, 6).map((t) => (
              <p key={t._id} className="text-[11px] text-gray-600">
                {t.side.toUpperCase()} {t.quantity} @ {fmtUSD(t.price)}
                {t.realized_pnl ? <span className={gainClass(t.realized_pnl)}> · {fmtUSD(t.realized_pnl)}</span> : null}
                <span className="text-gray-300"> · {new Date(t.created_at).toLocaleString()}</span>
              </p>
            ))}
            {!detail.trades?.length && <p className="text-[11px] text-gray-400">No fills yet.</p>}
          </div>
        </div>
      )}
    </Card>
  );
}

const Mini = ({ label, value, cls = "" }) => (
  <div className="rounded-xl bg-gray-50 py-2">
    <p className="text-[10px] text-gray-400">{label}</p>
    <p className={`text-sm font-semibold ${cls}`}>{value}</p>
  </div>
);

function BotEquityChart({ botId }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("ALL");
  useEffect(() => {
    api.get(`/bots/${botId}/equity`, { params: { range } }).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [botId, range]);
  if (data === null) return <p className="text-[11px] text-gray-400">Loading equity…</p>;
  if (!data || !data.points?.length) return null;
  const pts = data.points.map((p) => ({ ...p, ts: new Date(p.t).getTime() }));
  return (
    <div data-testid={`bot-equity-chart-${botId}`}>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          Equity curve <span className="text-[9px] bg-gray-100 rounded-full px-1.5 py-0.5 text-gray-500">PAPER</span>
        </p>
        <div className="flex items-center gap-0.5">
          {["1D", "1W", "1M", "3M", "ALL"].map((r) => (
            <button key={r} data-testid={`equity-range-${r}-${botId}`} onClick={() => setRange(r)}
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${range === r ? "bg-brand-tint text-brand-deep" : "text-gray-400 hover:bg-gray-100"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mb-1" data-testid={`bot-equity-summary-${botId}`}>
        {fmtUSD(data.starting_equity)} → <b>{fmtUSD(data.current_equity)}</b>
        {" "}(<span className={gainClass(data.total_return_pct)}>{data.total_return_pct >= 0 ? "+" : ""}{data.total_return_pct}%</span>)
        {" "}· realized {fmtUSD(data.realized_pnl)} · open {fmtUSD(data.unrealized_pnl)}
        {" "}· WR {data.win_rate}% · PF {data.profit_factor} · {data.trades_count} trades
        {" "}· <span className="text-rose-600" data-testid={`bot-equity-maxdd-${botId}`}>Max DD -{data.max_drawdown_pct}%</span>
      </p>
      <ResponsiveContainer width="100%" height={140}>
        <ComposedChart data={pts} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]} hide />
          <YAxis domain={["auto", "auto"]} hide />
          <Tooltip
            formatter={(v, name) => [fmtUSD(v), name === "peak" ? "Peak" : "Equity"]}
            labelFormatter={(ts) => new Date(ts).toLocaleString()}
            contentStyle={{ fontSize: 11, borderRadius: 8 }}
          />
          {/* peak (rose) behind equity — the visible rose band IS the drawdown */}
          <Area type="stepAfter" dataKey="peak" stroke="none" fill="#fecdd3" fillOpacity={0.8} isAnimationActive={false} />
          <Area type="monotone" dataKey="equity" stroke="hsl(var(--brand))" strokeWidth={1.5}
            fill="#ffffff" fillOpacity={1} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function OptimizerPanel({ bot, onChanged }) {
  const [opt, setOpt] = useState(bot.last_optimization || null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bots/${bot._id}/optimize`);
      setOpt(data);
      toast.success("AI experiment proposed — review before applying");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/bots/${bot._id}/apply-optimization`);
      setOpt((o) => ({ ...o, applied: true }));
      toast.success("Parameters updated — bot now runs the approved experiment");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const Cmp = ({ label, a, b, suffix = "" }) => (
    <div className="text-center rounded-lg bg-white border border-gray-100 py-1.5">
      <p className="text-[10px] text-gray-400">{label}</p>
      <p className="text-xs font-semibold">
        {a}{suffix} <span className="text-gray-300">→</span>{" "}
        <span className={b > a ? "text-emerald-600" : b < a ? "text-rose-600" : ""}>{b}{suffix}</span>
      </p>
    </div>
  );

  return (
    <div className="rounded-xl bg-brand-tint/40 p-3" data-testid={`optimizer-panel-${bot._id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-brand-deep flex items-center gap-1.5">
          <Beaker size={12} /> AI Strategy Optimizer
        </p>
        <Button size="sm" variant="outline" disabled={busy} onClick={run}
          data-testid={`optimize-btn-${bot._id}`} className="h-7 rounded-full text-xs px-3">
          {busy ? "Analyzing…" : opt ? "Re-analyze" : "Find a better setup"}
        </Button>
      </div>
      {opt && (
        <div className="mt-2 space-y-2" data-testid={`optimizer-result-${bot._id}`}>
          <p className="text-[11px] text-gray-700">{opt.finding}</p>
          {opt.suggestion && <p className="text-[11px] text-gray-600 italic">Experiment: {opt.suggestion}</p>}
          {opt.weak_hours?.length > 0 && (
            <p className="text-[11px] text-amber-700">Weak entry hours (ET): {opt.weak_hours.join(", ")}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            <Cmp label="Return" a={opt.baseline_backtest.total_return_pct} b={opt.proposed_backtest.total_return_pct} suffix="%" />
            <Cmp label="Win rate" a={opt.baseline_backtest.win_rate} b={opt.proposed_backtest.win_rate} suffix="%" />
            <Cmp label="Sharpe" a={opt.baseline_backtest.sharpe} b={opt.proposed_backtest.sharpe} />
            <Cmp label="Max DD" a={opt.baseline_backtest.max_drawdown_pct} b={opt.proposed_backtest.max_drawdown_pct} suffix="%" />
          </div>
          <p className="text-[10px] text-gray-500 font-mono">
            {JSON.stringify(opt.current_params)} → {JSON.stringify(opt.proposed_params)}
          </p>
          {opt.applied ? (
            <Badge className="rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">APPLIED</Badge>
          ) : (
            <Button size="sm" disabled={busy} onClick={apply}
              data-testid={`apply-optimization-btn-${bot._id}`}
              className="h-7 rounded-full text-xs px-3 bg-emerald-600 hover:bg-emerald-700 text-white">
              Apply proposed params
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CreateBotWizard({ onDone }) {
  const [assetType, setAssetType] = useState("crypto");
  const [symbol, setSymbol] = useState("BTC");
  const [strategy, setStrategy] = useState("sma_cross");
  const [form, setForm] = useState({
    name: "", capital: "5000", risk_per_trade_pct: "1", max_daily_loss_pct: "3",
    stop_loss_pct: "1.5", take_profit_pct: "", trailing_stop_pct: "1", breakeven_at_pct: "",
  });
  const [scaleOut, setScaleOut] = useState([
    { at_pct: "3", portion_pct: "25" },
    { at_pct: "", portion_pct: "" },
    { at_pct: "", portion_pct: "" },
  ]);
  const setStep = (i, k) => (e) =>
    setScaleOut((s) => s.map((row, idx) => (idx === i ? { ...row, [k]: e.target.value } : row)));
  const [bt, setBt] = useState(null);
  const [busy, setBusy] = useState(false);
  const isCouncil = strategy === "ai_council";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const params = STRATEGIES.find((s) => s.id === strategy)?.params || {};

  const runBacktest = async () => {
    setBusy(true);
    setBt(null);
    try {
      const { data } = await api.post("/bots/backtest", { asset_type: assetType, symbol, strategy, params, days: 30 });
      setBt(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deploy = async () => {
    setBusy(true);
    try {
      const steps = scaleOut
        .filter((s) => s.at_pct && s.portion_pct)
        .map((s) => ({ at_pct: parseFloat(s.at_pct), portion_pct: parseFloat(s.portion_pct) }));
      await api.post("/bots", {
        name: form.name || `${symbol} ${STRATEGIES.find((s) => s.id === strategy)?.label}`,
        asset_type: assetType, symbol, strategy, params,
        capital: parseFloat(form.capital),
        risk_per_trade_pct: parseFloat(form.risk_per_trade_pct),
        max_daily_loss_pct: parseFloat(form.max_daily_loss_pct),
        stop_loss_pct: parseFloat(form.stop_loss_pct),
        take_profit_pct: form.take_profit_pct ? parseFloat(form.take_profit_pct) : null,
        trailing_stop_pct: form.trailing_stop_pct ? parseFloat(form.trailing_stop_pct) : null,
        breakeven_at_pct: form.breakeven_at_pct ? parseFloat(form.breakeven_at_pct) : null,
        partial_take_profits: steps.length ? steps : null,
      });
      toast.success("Bot deployed — paper trading is live");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border border-brand/30 rounded-2xl p-6 space-y-5" data-testid="create-bot-wizard">
      <h2 className="text-base font-semibold">New AutoPilot bot</h2>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="text-xs text-gray-500">Asset class</label>
          <div className="flex gap-1 mt-1">
            {["crypto", "stock", "future"].map((a) => (
              <button key={a} data-testid={`wizard-asset-${a}`}
                onClick={() => { setAssetType(a); setSymbol(SYMBOLS[a][0]); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${assetType === a ? "bg-brand text-white" : "bg-gray-100 text-gray-600"}`}>
                {a}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Symbol</label>
          <div className="flex gap-1 mt-1 flex-wrap">
            {SYMBOLS[assetType].map((sym) => (
              <button key={sym} data-testid={`wizard-symbol-${sym}`} onClick={() => setSymbol(sym)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${symbol === sym ? "bg-brand-tint text-brand-deep ring-1 ring-brand/30" : "bg-gray-100 text-gray-600"}`}>
                {sym}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Bot name (optional)</label>
          <Input data-testid="wizard-name" value={form.name} onChange={set("name")} placeholder={`${symbol} bot`} className="mt-1" />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-500">Strategy</label>
        <div className="flex gap-1.5 mt-1 flex-wrap">
          {STRATEGIES.map((st) => (
            <button key={st.id} data-testid={`wizard-strategy-${st.id}`} onClick={() => { setStrategy(st.id); setBt(null); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${strategy === st.id ? "bg-brand text-white" : "bg-gray-100 text-gray-600"}`}>
              {st.label}
            </button>
          ))}
        </div>
        {isCouncil && (
          <p className="text-[11px] text-gray-500 mt-1.5">
            5 Claude agents analyze every ~15 min. The Council only proposes — every order still passes the deterministic risk engine.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <Field label="Capital $" testId="wizard-capital" value={form.capital} onChange={set("capital")} />
        <Field label="Risk/trade %" testId="wizard-risk" value={form.risk_per_trade_pct} onChange={set("risk_per_trade_pct")} />
        <Field label="Max daily loss %" testId="wizard-daily-loss" value={form.max_daily_loss_pct} onChange={set("max_daily_loss_pct")} />
        <Field label="Stop loss %" testId="wizard-stop" value={form.stop_loss_pct} onChange={set("stop_loss_pct")} />
        <Field label="Take profit %" testId="wizard-tp" value={form.take_profit_pct} onChange={set("take_profit_pct")} />
        <Field label="Trailing stop %" testId="wizard-trail" value={form.trailing_stop_pct} onChange={set("trailing_stop_pct")} placeholder="off" />
        <Field label="Break-even at %" testId="wizard-be" value={form.breakeven_at_pct} onChange={set("breakeven_at_pct")} placeholder="off" />
      </div>

      <div>
        <label className="text-xs text-gray-500">Scale-out (partial take profits — remainder trails)</label>
        <div className="flex gap-3 flex-wrap mt-1" data-testid="wizard-scaleout">
          {scaleOut.map((row, i) => (
            <div key={i} className="flex items-center gap-1.5 rounded-xl bg-gray-50 px-2.5 py-1.5">
              <span className="text-[10px] text-gray-400 font-semibold">#{i + 1}</span>
              <Input data-testid={`wizard-so-at-${i}`} type="number" value={row.at_pct} onChange={setStep(i, "at_pct")}
                placeholder="target %" className="h-8 w-20 text-xs" />
              <span className="text-[10px] text-gray-400">sell</span>
              <Input data-testid={`wizard-so-portion-${i}`} type="number" value={row.portion_pct} onChange={setStep(i, "portion_pct")}
                placeholder="%" className="h-8 w-16 text-xs" />
            </div>
          ))}
        </div>
        <p className="text-[10px] text-gray-400 mt-1">e.g. sell 25% at +3%, 25% at +6% — portions must total under 100%.</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {!isCouncil && (
          <Button data-testid="wizard-backtest-btn" onClick={runBacktest} disabled={busy} variant="outline" className="rounded-full">
            <Beaker size={14} className="mr-1.5" /> {busy && !bt ? "Backtesting…" : "Backtest 30d"}
          </Button>
        )}
        <Button data-testid="wizard-deploy-btn" onClick={deploy} disabled={busy || (!isCouncil && !bt)}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
          <Rocket size={14} className="mr-1.5" /> Deploy paper bot
        </Button>
        {!isCouncil && !bt && <p className="text-[11px] text-gray-400">Backtest first — the same strategy code drives both.</p>}
      </div>

      {bt && (
        <div className="rounded-xl bg-gray-50 p-4" data-testid="wizard-backtest-results">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Backtest — {bt.symbol} · {bt.days}d</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <Mini label="Return" value={`${bt.total_return_pct >= 0 ? "+" : ""}${bt.total_return_pct?.toFixed(1)}%`} cls={gainClass(bt.total_return_pct)} />
            <Mini label="Buy & hold" value={`${bt.buy_hold_return_pct >= 0 ? "+" : ""}${bt.buy_hold_return_pct?.toFixed(1)}%`} />
            <Mini label="Win rate" value={`${bt.win_rate?.toFixed(0)}%`} />
            <Mini label="Max DD" value={`${bt.max_drawdown_pct?.toFixed(1)}%`} />
            <Mini label="Sharpe" value={bt.sharpe?.toFixed(2)} />
          </div>
        </div>
      )}
    </Card>
  );
}

const Field = ({ label, value, onChange, testId, placeholder }) => (
  <div>
    <label className="text-[11px] text-gray-500">{label}</label>
    <Input data-testid={testId} type="number" value={value} onChange={onChange} placeholder={placeholder} className="mt-1 h-9 text-sm" />
  </div>
);
